import { defineComponent as _defineComponent } from 'vue'
import { ref, computed, nextTick, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { Dialog, DialogPanel, DialogTitle, TransitionChild, TransitionRoot } from '@headlessui/vue';

import AuraChatLayout from '@/views/pages/aura/AuraChatLayout.vue';
import AuraSidebar from '@/views/pages/aura/components/AuraSidebar.vue';
import AuraHeader from '@/views/pages/aura/components/AuraHeader.vue';
import AuraValidationMessage from '@/views/pages/aura/components/AuraValidationMessage.vue';
import TemporalWarning from '@/views/pages/aura/components/TemporalWarning.vue';
import { useAuraStore } from '@/stores/modules/aura';
import { AuraService } from '@/services';
import { useAuraToast } from '@/composables/useAuraToast';
import { useAuraResponsive } from '@/composables/useAuraResponsive';
import { can } from '@/composables/useAuth';
import { Permission } from '@/types';
import type { AuraConversation, AuraContext, AuraState } from '@/types';

const MAX_MESSAGE_LENGTH = 2000;

// Local UI state only

export default /*@__PURE__*/_defineComponent({
  __name: 'AuraComposer',
  setup(__props, { expose: __expose }) {
  __expose();

/**
 * AURA Composer - Layout container orchestrating sidebar, header, and chat
 * SRP: Only responsible for layout composition and event delegation
 */
const { t } = useI18n();
const store = useAuraStore();
const { info: showInfoToast } = useAuraToast();
const { isMobile } = useAuraResponsive();
const isSending = ref(false);
const isHistoryLoading = ref(false);
const isContextLoading = ref(true);
const auraContext = ref<AuraContext | null>(null);
const chatLayoutRef = ref<InstanceType<typeof AuraChatLayout> | null>(null);
const isInternalLoading = computed(
    () => store.isLoading || isSending.value || isHistoryLoading.value || isContextLoading.value,
);
const deleteDialogOpen = ref(false);
const conversationToDelete = ref<AuraConversation | null>(null);
const sidebarOpen = ref(false);

watch(
    isMobile,
    (mobile) => {
        sidebarOpen.value = !mobile;
    },
    { immediate: true },
);

const openSidebar = () => {
    sidebarOpen.value = true;
};

const closeSidebar = () => {
    if (isMobile.value) sidebarOpen.value = false;
};

// Permissions
const canViewHistory = computed(() => can(Permission.VIEW_AURA_CONVERSATION));
const canUseAura = computed(() => can(Permission.VIEW_AURA));
const canDeleteConversation = computed(() => can(Permission.DELETE_AURA_CONVERSATION));
const isAuraAvailable = computed(() => canUseAura.value && auraContext.value?.enabled === true);

// Computed from store for message display
const messages = computed(() => store.messages);
const currentConversationId = computed(() => store.currentConversation?.id || null);
const suggestedQuestions = computed(() =>
    (auraContext.value?.suggestedQuestions ?? []).map((item) => item.questionText),
);
const validationError = computed(() => store.validationError);
const temporalConflict = computed(() => store.temporalConflict);
const loadingMessage = computed(() => {
    if (store.validationStep === 'idle') {
        return t('aura.chat.loading');
    }
    return t(`aura.chat.loadingSteps.${store.validationStep}`);
});

// Load history on mount (only if has permission)
onMounted(async () => {
    isContextLoading.value = true;
    try {
        const response = await AuraService.getContext();
        auraContext.value = response.data;
    } catch (error) {
        console.error('Failed to load AURA context:', error);
        auraContext.value = null;
    } finally {
        isContextLoading.value = false;
    }

    if (!canViewHistory.value) return;
    isHistoryLoading.value = true;
    try {
        await store.loadHistory();
    } catch (error) {
        console.error('Failed to load history:', error);
    } finally {
        isHistoryLoading.value = false;
    }
});

// Event handlers - delegate to store
const handleSelectConversation = async (conversation: AuraConversation) => {
    try {
        await store.loadConversation(conversation.id);
    } catch (error) {
        console.error('Failed to load conversation:', error);
    }
};

const handleCreateConversation = async () => {
    try {
        await store.createConversation();
    } catch (error) {
        console.error('Failed to create conversation:', error);
    }
};

const handleRetryFailedQuestion = async (payload: {
    userMsgIndex: number;
    auraMsgIndex: number;
    question: string;
}) => {
    store.clearValidationError();
    store.clearTemporalConflict();
    store.setValidationStep('idle');

    const conversationId = currentConversationId.value;
    if (!conversationId) return;

    const userMsg = store.messages[payload.userMsgIndex];
    const auraMsg = store.messages[payload.auraMsgIndex];

    const idsToDelete: number[] = [];
    if (userMsg && typeof userMsg.id === 'number') {
        idsToDelete.push(userMsg.id);
    }
    if (auraMsg && typeof auraMsg.id === 'number') {
        idsToDelete.push(auraMsg.id);
    }

    try {
        await store.deleteConversationMessages(conversationId, idsToDelete);
    } catch (error) {
        console.error('Failed to delete failed messages from history:', error);
    }
};

const handleSendMessage = async (content: string) => {
    if (content.trim().length > MAX_MESSAGE_LENGTH) {
        store.setValidationError({
            type: 'MESSAGE_TOO_LONG',
            message: `Sua mensagem excede o limite de ${MAX_MESSAGE_LENGTH} caracteres.`,
            suggestion: 'Resuma a pergunta ou envie em partes menores.',
        });
        showInfoToast(`Limite de ${MAX_MESSAGE_LENGTH} caracteres excedido.`);
        return;
    }

    // Create conversation if not exists, passing the message as initialQuestion
    if (!currentConversationId.value) {
        await store.createConversation(undefined, content);
    }

    store.clearValidationError();
    store.clearTemporalConflict();

    const filtersBeforeSend = store.activeFilters
        ? {
              ...store.activeFilters,
          }
        : null;

    // Simulate validation progress steps sequentially
    let currentStepIndex = 0;
    const steps: Array<AuraState['validationStep']> = [
        'validating_scope',
        'classifying_intent',
        'checking_temporal',
        'processing',
    ];
    store.setValidationStep(steps[currentStepIndex]);

    let stepInterval: ReturnType<typeof setInterval> | null = null;
    stepInterval = setInterval(() => {
        if (currentStepIndex < steps.length - 1) {
            currentStepIndex++;
            store.setValidationStep(steps[currentStepIndex]);
        } else if (stepInterval) {
            clearInterval(stepInterval);
        }
    }, 5000);

    isSending.value = true;
    try {
        const response = await store.sendMessage(content);
        if (store.consumeAuxiliaryFilterLoadFailure()) {
            showInfoToast(t('aura.chat.auxiliaryFiltersUnavailable'));
        }
        const effectiveDateWindow = response.metadata?.filterResolution?.effectiveDateWindow;
        const dateAdjusted =
            response.metadata?.filterResolution?.source === 'question' &&
            !!effectiveDateWindow &&
            (filtersBeforeSend?.dateFrom !== effectiveDateWindow.dateFrom ||
                filtersBeforeSend?.dateTo !== effectiveDateWindow.dateTo);

        const responseClassification = response.response.classification;
        const metadataClassification = response.metadata?.classification;
        const effectiveClassification = responseClassification || metadataClassification;
        const apiConflict = effectiveClassification?.time_conflict;

        if (effectiveClassification?.time_coherent === false && apiConflict) {
            store.clearValidationError();
            store.setTemporalConflict({
                questionPeriod:
                    response.metadata?.filterResolution?.questionPeriod?.original ||
                    apiConflict.questionPeriod ||
                    effectiveClassification.entities?.period ||
                    '',
                filterPeriod: response.metadata?.filterResolution?.effectiveDateWindow
                    ? `${response.metadata.filterResolution.effectiveDateWindow.dateFrom || ''} - ${response.metadata.filterResolution.effectiveDateWindow.dateTo || ''}`
                    : apiConflict.filterPeriod || '',
                details: apiConflict.details || undefined,
                suggestedDateFrom:
                    response.metadata?.filterResolution?.questionDateWindow?.dateFrom || undefined,
                suggestedDateTo:
                    response.metadata?.filterResolution?.questionDateWindow?.dateTo || undefined,
            });
        }

        // --- filter_conflict: agent / queue disambiguation ---
        const filterConflict =
            response.response?.status?.filter_conflict ||
            effectiveClassification?.filter_conflict ||
            null;
        const operatorBeforeSend = filtersBeforeSend?.operatorId || null;
        const operatorAfterSend = store.activeFilters?.operatorId || null;
        const selectedAgent = store.availableAgents.find((agent) => agent.id === operatorAfterSend);
        let filterAdjustmentToastMessage = '';

        if (filterConflict) {
            if (filterConflict.status === 'resolved') {
                const label = filterConflict.type === 'agent' ? 'Agente' : 'Fila';
                filterAdjustmentToastMessage = `${label} "${filterConflict.resolvedName}" selecionado automaticamente.`;
            } else if (filterConflict.status === 'not_found') {
                store.setValidationError({
                    type: 'FILTER_NOT_FOUND',
                    message: `Não encontrei nenhum ${filterConflict.type === 'agent' ? 'agente' : 'fila'} com o nome "${filterConflict.extractedValue}".`,
                    suggestion: '',
                });
            }
        }

        if (!filterAdjustmentToastMessage && operatorBeforeSend !== operatorAfterSend) {
            filterAdjustmentToastMessage = selectedAgent
                ? `Agente "${selectedAgent.name}" selecionado automaticamente.`
                : 'Filtro de agente removido automaticamente.';
        }

        if (filterAdjustmentToastMessage) {
            showInfoToast(filterAdjustmentToastMessage);
        }

        if (
            response.response.status?.status === 'blocked' &&
            response.response.status.code !== 'TIME_CONFLICT'
        ) {
            store.setValidationError({
                type: response.response.status.code || 'INVALID_INTENT',
                message:
                    response.response.status.message ||
                    response.response.summary?.content ||
                    t('aura.validation.genericMessage'),
                suggestion:
                    (response.response.status.details?.suggestion as string | undefined) || '',
            });
        }

        if (dateAdjusted) {
            showInfoToast(t('aura.chat.dateAdjustedByAura'));
        }
    } catch (error) {
        console.error('Failed to get AURA response:', error);
        // Add error message to chat as styled message
        if (store.error) {
            store.addErrorMessage(store.error);
            store.clearError();
        }
    } finally {
        if (stepInterval) {
            clearInterval(stepInterval);
        }
        if (!store.temporalConflict) {
            store.setValidationStep('idle');
        }
        isSending.value = false;
    }
};

const handleSendWithFilter = async (payload: {
    message: string;
    filterKey: string;
    filterId: number;
}) => {
    const overrideFilters = {
        ...(store.activeFilters || {}),
        [payload.filterKey]: payload.filterId,
    };
    store.setFilters(overrideFilters);
    await handleSendMessage(payload.message);
};

const handleAutoCorrect = () => {
    const suggestedDateFrom = store.temporalConflict?.suggestedDateFrom;
    const suggestedDateTo = store.temporalConflict?.suggestedDateTo;

    if (!suggestedDateFrom || !suggestedDateTo) {
        return;
    }

    store.setFilters({
        ...(store.activeFilters || {}),
        dateFrom: suggestedDateFrom,
        dateTo: suggestedDateTo,
    });
    store.clearTemporalConflict();
    store.setValidationStep('idle');
    showInfoToast(t('aura.chat.temporalConflictAutoCorrected'));
};

const handleManualEdit = () => {
    store.clearTemporalConflict();
    store.setValidationStep('idle');
    showInfoToast(t('aura.chat.temporalConflictManualEdit'));
};

const handleProceed = () => {
    store.clearTemporalConflict();
    store.setValidationStep('idle');
};

const handleRephrase = async () => {
    store.clearTemporalConflict();
    store.setValidationStep('idle');
    await nextTick();
    chatLayoutRef.value?.focusComposer();
};

const handleDismissTemporalWarning = () => {
    store.clearTemporalConflict();
    store.setValidationStep('idle');
};

const handleDismissValidationError = () => {
    store.clearValidationError();
};

const handleDeleteConversation = async (conversation: AuraConversation) => {
    if (!canDeleteConversation.value) return;

    conversationToDelete.value = conversation;
    deleteDialogOpen.value = true;
};

const confirmDeleteConversation = async () => {
    if (!conversationToDelete.value) return;

    try {
        await store.deleteConversation(conversationToDelete.value.id);
    } catch (error) {
        console.error('Failed to delete conversation:', error);
    } finally {
        deleteDialogOpen.value = false;
        conversationToDelete.value = null;
    }
};

const cancelDeleteConversation = () => {
    deleteDialogOpen.value = false;
    conversationToDelete.value = null;
};

const __returned__ = { t, store, showInfoToast, isMobile, MAX_MESSAGE_LENGTH, isSending, isHistoryLoading, isContextLoading, auraContext, chatLayoutRef, isInternalLoading, deleteDialogOpen, conversationToDelete, sidebarOpen, openSidebar, closeSidebar, canViewHistory, canUseAura, canDeleteConversation, isAuraAvailable, messages, currentConversationId, suggestedQuestions, validationError, temporalConflict, loadingMessage, handleSelectConversation, handleCreateConversation, handleRetryFailedQuestion, handleSendMessage, handleSendWithFilter, handleAutoCorrect, handleManualEdit, handleProceed, handleRephrase, handleDismissTemporalWarning, handleDismissValidationError, handleDeleteConversation, confirmDeleteConversation, cancelDeleteConversation, get Dialog() { return Dialog }, get DialogPanel() { return DialogPanel }, get DialogTitle() { return DialogTitle }, get TransitionChild() { return TransitionChild }, get TransitionRoot() { return TransitionRoot }, AuraChatLayout, AuraSidebar, AuraHeader, AuraValidationMessage, TemporalWarning }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})